import { Injectable } from '@angular/core';
import { Client, Contact, User } from '@mitel/cloudlink-sdk/admin';
import { Subscription, ReplaySubject, Subject } from 'rxjs';

import { ChatAuthService } from './chat-auth.service';
import { SharedHelpers } from '../classes/sharedhelpers';
import { TranslateService } from '@ngx-translate/core';
import { ChatLoggingService } from './chat-logging.service';
import { ChatCloudLinkService } from './chat-cloudlink.service';
import { filter } from 'rxjs/operators';
import { ChatDatabaseService } from './chat-database.service';
import { ChatUserDataService } from './chat-user-data.service';
import { Entities, UserAvatars } from '@mitel/cloudlink-sdk/admin';
import chunk from 'lodash-es/chunk';
import { Participant } from '@mitel/cloudlink-sdk/chat';

export class ChatPrincipal {
  principalId: string;
  avatars?: UserAvatars;
  email?: string;
  name: string;
  entityType: Entities.EntityTypeEnum;
  phone?: string;
  mobile?: string;
  extension?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatAdminService {
  //private auth: Authentication;
  //private api: Admin.AdminApi;
  //private authUrl: string;
  //private adminUrl: string;
  public avatars: { [principalId: string]: UserAvatars } = {};
  // Cloudlink's own avatars, kept apart from the host avatars applied over them in avatars (see resolveAvatars)
  private cloudlinkAvatars: { [principalId: string]: UserAvatars } = {};
  public displayNames: { [principalId: string]: string } = {};
  public emails: { [principalId: string]: string } = {};
  public phoneNumbers: { [principalId: string]: string } = {};
  public mobileNumbers: { [principalId: string]: string } = {};
  public extensions: { [principalId: string]: string } = {};
  private principalCache: { [principalId: string]: ChatPrincipal } = {};
  private retryCache: { [principalId: string]: boolean } = {};
  private retryCount = 0; //To limit retries in case of a consistent failure on the platform
  private retryTimer = null;
  private replacedUsers = {};
  private contactsList = [];
  public usersLoaded$ = {};
  public useNicknames = false;
  public reverseNames = false;
  public userChanged$: Subject<string> = new Subject<string>();
  public participantsLoaded$?: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private chatAuthService: ChatAuthService,
    private translateService: TranslateService,
    private chatLoggingService: ChatLoggingService,
    private chatCloudLinkService: ChatCloudLinkService,
    private chatDatabaseService: ChatDatabaseService,
    private chatUserDataService: ChatUserDataService
  ) {
    this.chatUserDataService.dataChange$.subscribe(({ userId }) => {
      const avatars = this.resolveAvatars(userId);
      // userChanged$ re-renders every chat item, so skip it when the avatar didn't change (e.g. a presence/status update)
      if (avatars?.[50] === this.avatars[userId]?.[50] && avatars?.[150] === this.avatars[userId]?.[150]) {
        return;
      }
      this.avatars[userId] = avatars;
      this.userChanged$.next(userId);
    });

    const authSub = new Subscription();
    authSub.add(
      this.chatAuthService.ready$.subscribe(
        () => {
          authSub.unsubscribe();
          this.initialize();
        },
        er => {
          //Let chat component handle error
        }
      )
    );
  }

  private startRetryTimer() {
    if (this.retryTimer) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
    this.chatLoggingService.info({
      logMessage: 'chat.admin.service.startRetryTimer(): starting timer'
    });
    this.retryTimer = setInterval(() => {
      if (this.retryCount == 3) {
        this.chatLoggingService.info({
          logMessage: 'chat.admin.service.startRetryTimer(): resetting timer, unable to resync'
        });
        this.resetRetryTimer();
        return;
      }
      let userIds = Object.keys(this.retryCache);
      if (userIds.length) {
        this.chatLoggingService.info({
          logMessage: 'chat.admin.service.startRetryTimer(): retrying getPrincipals'
        });
        let userChunks = chunk(userIds, 40);
        userChunks.forEach(c => {
          this.getPrincipals(c);
        });
        this.retryCount++;
      } else {
        this.chatLoggingService.info({
          logMessage: 'chat.admin.service.startRetryTimer(): resetting timer, no userIds'
        });
        this.resetRetryTimer();
      }
    }, 60000);
  }

  private resetRetryTimer() {
    this.chatLoggingService.info({
      logMessage: 'chat.admin.service.resetRetryTimer()'
    });
    if (this.retryTimer) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
    this.retryCount = 0;
  }

  public initialize() {
    if (this.chatCloudLinkService.jsonMessagesAsSource || this.chatCloudLinkService.aiMode) {
      this.chatLoggingService.info({
        logMessage: 'chat-admin.service.initialize() jsonMessagesAsSource/aiMode enabled - skipping'
      });
      return;
    }

    this.chatCloudLinkService.usersChanged$
      .pipe(filter(user => !!this.usersLoaded$[user.userId]))
      .subscribe(async resource => {
        try {
          if (resource.userId === this.chatAuthService.userId) {
            this.chatAuthService.user = resource;
          }
          await this.getPrincipal(resource.userId, true, resource);
          this.userChanged$.next(resource.userId);
        } catch (ex) {
          // stifle exception to prevent unsubscribing
          this.chatLoggingService.error({
            logMessage: 'chat-admin.service.initialize(): Error in subscribe usersChanged',
            logParameters: [ex]
          });
        }
      });

    this.chatCloudLinkService.getUser$.pipe(filter(userId => !this.usersLoaded$[userId])).subscribe(async userId => {
      try {
        await this.getPrincipal(userId);
        this.userChanged$.next(userId);
      } catch (ex) {
        // stifle exception to prevent unsubscribing
        this.chatLoggingService.error({
          logMessage: 'chat-admin.service.initialize(): Error in subscribe getUser',
          logParameters: [ex]
        });
      }
    });

    this.chatCloudLinkService.contactsChanged$.subscribe(async resource => {
      try {
        await this.getPrincipal(resource.contactId, true, resource);
        this.userChanged$.next(resource.contactId);
      } catch (ex) {
        // stifle exception to prevent unsubscribing
        this.chatLoggingService.error({
          logMessage: 'chat-admin.service.initialize(): Error in subscribe contactsChanged',
          logParameters: [ex]
        });
      }
    });
  }

  public async getPrincipals(principalIds: string[], waitForDb = false, reload = false) {
    this.chatLoggingService.debug({
      logMessage: 'chat.admin.service.getPrincipals()',
      logParameters: [principalIds]
    });
    let principalFindList = [];
    let principalsToFind = [];
    principalIds.forEach(p => {
      if (this.principalCache[p] === undefined || this.retryCache[p] || reload) {
        principalFindList.push(p);
        principalsToFind.push(p);
        this.principalCache[p] = null;
        this.usersLoaded$[p] = new ReplaySubject(1);
      }
    });
    let errorLoading = false;
    if (principalsToFind.length && this.chatCloudLinkService.admin) {
      try {
        this.chatLoggingService.debug({
          logMessage: 'chat.admin.service.getPrincipals() fetching',
          logParameters: [principalsToFind]
        });
        //Have to get users first as entities uses uniqueUserId at the moment
        let userItems = principalsToFind.map(p => encodeURIComponent(p)).join(',');
        const users = await this.chatCloudLinkService.admin
          .getUsers({
            items: userItems,
            itemKeys: 'userId'
          })
          .then(res => res?.body);

        if (users?._embedded?.items) {
          users._embedded.items.forEach(u => {
            this.principalCache[u.userId] = this.convertUserToChatPrincipal(u);
            this.setUserAvatarNameEmailLang(u.userId);
            principalsToFind.splice(principalsToFind.indexOf(u.userId), 1);
          });
        }
        let entityItems = principalsToFind.join(',');
        if (entityItems) {
          const entities = await this.chatCloudLinkService.admin
            .getEntities({
              items: entityItems,
              itemKeys: 'contactId, clientId'
            })
            .then(res => res?.body);

          if (entities?._embedded?.items) {
            entities._embedded.items.forEach(e => {
              if (principalsToFind.indexOf(e.entityId) > -1) {
                principalsToFind.splice(principalsToFind.indexOf(e.entityId), 1);
              }
              if (e.entityType === 'contact' && this.principalCache[e.entityId]?.entityType !== 'user') {
                this.principalCache[e.entityId] = this.convertEntityToChatPrincipal(e);
                if (reload) {
                  this.chatLoggingService.info({
                    logMessage: 'chat.admin.service.getPrincipals() on reload',
                    logParameters: [e.entityId, e.entityType, e.name]
                  });
                }
                if (this.contactsList.indexOf(e.entityId) === -1) {
                  this.contactsList.push(e.entityId);
                }
                this.setUserAvatarNameEmailLang(e.entityId);
              } else if (
                e.entityType === 'client' &&
                this.principalCache[e.entityId]?.entityType !== 'user' &&
                this.principalCache[e.entityId]?.entityType !== 'contact'
              ) {
                this.principalCache[e.entityId] = this.convertEntityToChatPrincipal(e);
                this.setUserAvatarNameEmailLang(e.entityId);
              }
            });
          }
        }
      } catch (ex) {
        errorLoading = true;
        this.chatLoggingService.error({
          logMessage: 'chat.admin.service.getPrincipals() error',
          logParameters: [ex]
        });
      }

      if (waitForDb) {
        for (let p of principalFindList) {
          await this.completePrincipalLoad(p, errorLoading);
        }
      } else {
        principalFindList.forEach(p => {
          this.completePrincipalLoad(p, errorLoading);
        });
      }

      if (this.retryTimer) {
        this.chatAuthService.triggerChangeDetection$.next();
      }
      if (errorLoading && !this.retryTimer) {
        this.startRetryTimer();
      }

      this.chatLoggingService.debug({
        logMessage: 'chat.admin.service.getPrincipals() complete'
      });
    }
  }

  /**
   * Populates the principal cache with ChatPrincipal objects converted from the given JSON payload of participants.
   * Unlike {@link getPrincipals}, which fetches principal data from the backend or cache, this method directly
   * processes an array of Participant objects (typically from a chat payload) and updates the cache accordingly.
   * Also sets user avatar, name, email, and language for each participant.
   * @param participants Array of Participant objects to convert and cache.
   */
  public getPrincipalsFromJsonPayload(participants: Participant[]) {
    participants.forEach(p => {
      this.principalCache[p.userId] = this.convertParticipantToChatPrincipal(p);
      this.setUserAvatarNameEmailLang(p.userId);
    });
  }

  public async getPrincipal(
    principalId: string,
    nocache = false,
    principal: User | Contact = null
  ): Promise<User | Client> {
    if ((!nocache || this.replacedUsers[principalId]) && this.principalCache[principalId] !== undefined) {
      return this.principalCache[principalId];
    }
    if (this.chatCloudLinkService.isOffline()) {
      this.setAvatars(principalId, null);
      this.emails[principalId] = null;
      this.phoneNumbers[principalId] = null;
      this.mobileNumbers[principalId] = null;
      this.extensions[principalId] = null;
      this.displayNames[principalId] = SharedHelpers.validateE164Num(principalId)
        ? this.formatNumber(principalId)
        : this.translateService.instant('cl_chat.unknownUser');
      if (!!SharedHelpers.oneToOneChatDisplayName) {
        this.displayNames[principalId] = SharedHelpers.oneToOneChatDisplayName;
      }

      if (this.displayNames[principalId] === this.translateService.instant('cl_chat.unknownUser')) {
        this.chatLoggingService.info({
          logMessage: 'chat.admin.service.getPrincipal() Unknown User set',
          logParameters: [principalId]
        });
      }

      this.usersLoaded$[principalId] = new ReplaySubject(1);
      this.usersLoaded$[principalId].next(true);
      return;
    }
    //Prevent multiple web requests for the same principalId
    this.principalCache[principalId] = null;

    let principalDeleted = false;
    if (!!principal && (principal as Contact).contactId && !(principal as Contact).contactType) {
      // fix: CL-25668 When an External Contact Is Deleted
      this.chatLoggingService.info({
        logMessage: 'chat.admin.service.getPrincipal() External Contact deleted',
        logParameters: [principalId, this.displayNames[principalId]]
      });
      principal = null;
      principalDeleted = true;
    }

    if (!this.usersLoaded$[principalId]) {
      // CL-16306: Avoid setting RelaySubject for the same principalId, as this can lead to missing event subscriptions in the chat.component.
      // Specifically, the observable this.chatAdminService.usersLoaded$ is affected,
      // causing fetchMessage to not execute, resulting chat stuck in loading state
      this.usersLoaded$[principalId] = new ReplaySubject(1);
      this.chatLoggingService.info({
        logMessage: 'chat.admin.service.getPrincipal() fetching - set RelaySubject',
        logParameters: [principalId, nocache, !!principal]
      });
    }

    let errorLoading = false;
    try {
      if (principal) {
        this.principalCache[principalId] = this.convertUserToChatPrincipal(principal);
      } else {
        let foundUser = false;
        //Have to get users first as entities uses uniqueUserId at the moment
        const users = await this.chatCloudLinkService.admin
          .getUsers({
            items: principalId,
            itemKeys: 'userId'
          })
          .then(res => res?.body);

        if (users?._embedded?.items) {
          foundUser = true;
          this.principalCache[principalId] = this.convertUserToChatPrincipal(users._embedded.items[0]);
          this.setUserAvatarNameEmailLang(principalId);
        }
        if (!foundUser) {
          const entities = await this.chatCloudLinkService.admin
            .getEntities({
              items: principalId,
              itemKeys: 'contactId, clientId'
            })
            .then(res => res?.body);

          if (entities?._embedded?.items) {
            this.principalCache[principalId] = this.convertEntityToChatPrincipal(entities._embedded.items[0]);
            if (
              this.principalCache[principalId]?.entityType === 'contact' &&
              this.contactsList.indexOf(principalId) === -1
            ) {
              this.contactsList.push(principalId);
            }
          }
        }
      }
      this.setUserAvatarNameEmailLang(principalId);
    } catch (ex) {
      errorLoading = true;
      this.chatLoggingService.error({
        logMessage: 'chat.admin.service.getPrincipal() error',
        logParameters: [ex]
      });
    }

    this.completePrincipalLoad(principalId, errorLoading, principalDeleted);

    if (errorLoading && !this.retryTimer) {
      this.startRetryTimer();
    }

    this.chatLoggingService.debug({
      logMessage: 'chat.admin.service.getPrincipal() loaded',
      logParameters: [principalId, nocache]
    });
    return this.principalCache[principalId];
  }

  private setUserAvatarNameEmailLang(principalId: string) {
    if (this.principalCache[principalId]) {
      this.setAvatars(principalId, this.principalCache[principalId].avatars || null);
      // Only users can have host data - contacts, clients and phone numbers have nothing to look up
      if (this.principalCache[principalId].entityType === 'user') {
        this.chatUserDataService.requestUsersData([principalId]);
      }

      if (this.principalCache[principalId].name) {
        this.displayNames[principalId] = this.principalCache[principalId].name;
      } else {
        this.displayNames[principalId] = SharedHelpers.validateE164Num(principalId)
          ? this.formatNumber(principalId)
          : this.translateService.instant('cl_chat.unknownUser');
        if (!!SharedHelpers.oneToOneChatDisplayName) {
          this.displayNames[principalId] = SharedHelpers.oneToOneChatDisplayName;
        }

        if (this.displayNames[principalId] === this.translateService.instant('cl_chat.unknownUser')) {
          this.chatLoggingService.info({
            logMessage: 'chat.admin.service.setUserAvatarNameEmailLang() Unknown User set',
            logParameters: [principalId]
          });
        }
      }

      if (this.reverseNames && this.displayNames[principalId]) {
        this.displayNames[principalId] = this.displayNames[principalId].split(' ').reverse().join(' ');
      }

      if (this.principalCache[principalId].email) {
        this.emails[principalId] = this.principalCache[principalId].email;
      } else {
        this.emails[principalId] = null;
      }

      if (this.principalCache[principalId].phone) {
        this.phoneNumbers[principalId] = this.principalCache[principalId].phone;
      } else {
        this.phoneNumbers[principalId] = null;
      }

      if (this.principalCache[principalId].mobile) {
        this.mobileNumbers[principalId] = this.principalCache[principalId].mobile;
      } else {
        this.mobileNumbers[principalId] = null;
      }

      if (this.principalCache[principalId].extension) {
        this.extensions[principalId] = this.principalCache[principalId].extension;
      } else {
        this.extensions[principalId] = null;
      }
    }
  }

  public setCacheValues(principalId: string, displayName: string, email: string, avatars: UserAvatars) {
    if (this.principalCache[principalId] === undefined) {
      this.displayNames[principalId] = SharedHelpers.validateE164Num(displayName)
        ? this.formatNumber(displayName)
        : displayName;
      this.emails[principalId] = email;
      this.setAvatars(principalId, avatars);
    }
  }

  private setAvatars(principalId: string, avatars: UserAvatars) {
    this.cloudlinkAvatars[principalId] = avatars;
    this.avatars[principalId] = this.resolveAvatars(principalId);
  }

  // Host avatar first, then Cloudlink's. A host null means the avatar was removed, so initials show rather than
  // bringing back the Cloudlink photo.
  private resolveAvatars(principalId: string): UserAvatars {
    const hostAvatar = this.chatUserDataService.getData(principalId)?.avatar;
    if (hostAvatar === null) {
      return null;
    }
    if (hostAvatar) {
      return {
        50: hostAvatar,
        150: hostAvatar
      };
    }
    return this.cloudlinkAvatars[principalId] ?? null;
  }

  private async completePrincipalLoad(principalId, setRetry = false, principalDeleted = false) {
    if (!setRetry && this.retryCache[principalId]) {
      delete this.retryCache[principalId];
    }
    if (this.principalCache[principalId]) {
      try {
        await this.chatDatabaseService.addUser(
          principalId,
          this.chatAuthService.accountId,
          this.displayNames[principalId],
          this.chatAuthService.userId === principalId,
          this.emails[principalId],
          // Host avatars are blob: URLs that die with the page, so only Cloudlink's are cached
          this.cloudlinkAvatars[principalId]
        );
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.admin.service.completePrincipalLoad(): Error adding user to db',
          logParameters: [principalId, ex]
        });
      }
    } else {
      if (
        !setRetry ||
        !this.displayNames[principalId] ||
        this.displayNames[principalId] === this.translateService.instant('cl_chat.unknownUser')
      ) {
        this.setAvatars(principalId, null);
        this.emails[principalId] = null;
        this.displayNames[principalId] = SharedHelpers.validateE164Num(principalId)
          ? this.formatNumber(principalId)
          : this.translateService.instant('cl_chat.unknownUser');
        if (!principalDeleted && !!SharedHelpers.oneToOneChatDisplayName) {
          this.displayNames[principalId] = SharedHelpers.oneToOneChatDisplayName;
        }

        if (this.displayNames[principalId] === this.translateService.instant('cl_chat.unknownUser')) {
          this.chatLoggingService.info({
            logMessage: 'chat.admin.service.completePrincipalLoad() Unknown User set',
            logParameters: [principalId]
          });
        }
      }

      if (setRetry) {
        this.retryCache[principalId] = true;
      }
      try {
        //TODO store unknown user boolean state in DB
        await this.chatDatabaseService.addUser(
          principalId,
          this.chatAuthService.accountId,
          this.translateService.instant('cl_chat.unknownUser'),
          this.chatAuthService.userId === principalId
        );
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.admin.service.completePrincipalLoad(): Error adding unknown user to db',
          logParameters: [principalId, ex]
        });
      }
    }
    this.usersLoaded$[principalId].next(true);
  }

  public async replaceUsers(userReplacements: {
    [userId: string]: { displayName: string; email: string; avatar: string };
  }) {
    for (let replace of Object.keys(userReplacements)) {
      let user = userReplacements[replace];
      if (!user) {
        this.setAvatars(replace, null);
        this.emails[replace] = null;
        this.displayNames[replace] = SharedHelpers.validateE164Num(replace)
          ? this.formatNumber(replace)
          : this.translateService.instant('cl_chat.unknownUser');
        if (!!SharedHelpers.oneToOneChatDisplayName) {
          this.displayNames[replace] = SharedHelpers.oneToOneChatDisplayName;
        }

        if (this.displayNames[replace] === this.translateService.instant('cl_chat.unknownUser')) {
          this.chatLoggingService.info({
            logMessage: 'chat.admin.service.replaceUsers() Unknown User set',
            logParameters: [replace]
          });
        }
      } else {
        this.setAvatars(
          replace,
          user.avatar
            ? {
                50: user.avatar,
                150: user.avatar
              }
            : null
        );
        this.emails[replace] = user.email;
        this.displayNames[replace] = SharedHelpers.validateE164Num(replace)
          ? this.formatNumber(replace)
          : user.displayName;
      }
      this.principalCache[replace] = null;
      this.replacedUsers[replace] = true;
      if (!this.usersLoaded$[replace]) {
        this.usersLoaded$[replace] = new ReplaySubject(1);
      }
      this.usersLoaded$[replace].next(true);
      try {
        await this.chatDatabaseService.addUser(
          replace,
          this.chatAuthService.accountId,
          this.displayNames[replace],
          this.chatAuthService.userId === replace
        );
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat.admin.service.replaceUsers(): Error adding user to db',
          logParameters: [replace, ex]
        });
      }
    }
  }

  private convertUserToChatPrincipal(user: User): ChatPrincipal {
    let name = user.name || user.displayName;
    if (this.useNicknames && user.nickName) {
      name = user.nickName;
    }
    return {
      principalId: user.userId,
      email: user.email,
      avatars: user.avatars ? user.avatars : null,
      name: name,
      entityType: 'user',
      phone: user.phoneVerified ? user.phone : null,
      mobile: user.mobileVerified ? user.mobile : null,
      extension: user.extension
    };
  }

  private convertEntityToChatPrincipal(entity: Entities): ChatPrincipal {
    return {
      principalId: entity.entityId,
      email: entity.email,
      avatars: entity.avatar
        ? {
            50: entity.avatar,
            150: entity.avatar
          }
        : null,
      name: entity.name,
      entityType: entity.entityType
    };
  }

  private convertParticipantToChatPrincipal(participant: Participant): ChatPrincipal {
    let userObj =
      participant &&
      participant._embedded &&
      participant._embedded.users &&
      typeof participant._embedded.users === 'object'
        ? participant._embedded.users
        : null;
    return {
      principalId: participant.userId,
      email: userObj && typeof userObj.email === 'string' ? userObj.email : undefined,
      avatars: null,
      name: userObj && typeof userObj.name === 'string' ? userObj.name : undefined,
      entityType: userObj && userObj.entityType ? userObj.entityType : 'contact'
    };
  }

  /**
   * Format the number
   * @param number value to format
   * @returns string
   */
  public formatNumber(number): string {
    return SharedHelpers.getFormatedLibNumber(number);
  }
}
